import { inject as service } from "@ember/service"
import { tracked } from "@glimmer/tracking"
import type { Course } from "@shared/models/course"
import type { FiscalGroup } from "@shared/models/fiscal-group"
import type { Group } from "@shared/models/group"
import type { KbEntry } from "@shared/models/kb-entry"
import type { Planbook } from "@shared/models/planbook"
import type { RotationCalendar } from "@shared/models/rotation-calendar"
import type EmberStore from "cc-frontend/services/ember-store"
import { Resource } from "ember-could-get-used-to-this"
import { isNil } from "lodash-es"

type ValueOf<T> = T[keyof T]

type RelationshipToModel = {
  group: Group
  fiscalGroup: FiscalGroup
  managingGroup: Group
  course: Course
  planbook: Planbook
  rotationCalendar: RotationCalendar
  // unfortunate -- this should be changed to look for the `type` field in the belongsTo
  child: KbEntry
}

interface Args<Type, RelationName> {
  positional: [model: Type | null, relationName: RelationName]
}

export default class BelongTo<
  Type extends ValueOf<RelationshipToModel>,
  RelationNames extends keyof Type["relationships"] & keyof RelationshipToModel,
  ReturnType extends RelationshipToModel[RelationNames]
> extends Resource<Args<Type, RelationNames>["positional"]> {
  @service("ember-store") declare store: EmberStore

  declare model: any | null
  declare relationName: string | null

  @tracked isPending = false
  @tracked isSettled = false
  @tracked isRejected = false
  @tracked isFulfilled = false
  @tracked content: ReturnType | null = null

  setup() {
    if (this.args.positional) {
      this.model = this.args.positional[0]
      this.relationName = this.args.positional[1]
    }
    this._find()
  }

  _find() {
    if (isNil(this.model) || isNil(this.relationName)) return
    this.isPending = true
    this.isFulfilled = false
    this.isSettled = false
    let relation = this.model.relationships[this.relationName].data
    if (isNil(relation)) return
    this.store
      .find(relation.type, relation.id)
      .then((doc: ReturnType) => {
        this.isPending = false
        this.isFulfilled = true
        this.content = doc
      })
      .catch(() => {
        this.isRejected = true
      })
      .finally(() => {
        this.isSettled = true
      })
  }

  update() {
    if (this.args.positional) {
      this.model = this.args.positional[0]
      this.relationName = this.args.positional[1]
    }
    this._find()
  }

  teardown() {
    // we'll unsubscribe from it here.
  }
}
